import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTenant } from "../lib/tenant";
import { useAuth } from "../lib/auth";
import { TopBar } from "../components/TopBar";
import { TenantQR } from "../components/TenantQR";
import { OcrTrainer } from "../components/OcrTrainer";
import { OcrManualEditor } from "../components/OcrManualEditor";
import { LoyaltyConfigEditor } from "../components/LoyaltyConfigEditor";
import { BrandingEditor } from "../components/BrandingEditor";
import { api, callableErrorMessage } from "../lib/api";
import { useAmbassadorApplications, useTenantAmbassadors, useTenantInvites } from "../lib/ambassador";
import { useTenantOcrConfig, useLoyaltyConfig } from "../lib/tenantConfig";
import { useTenantStats, useTenantRecentActivity } from "../lib/tenantStats";
import { useTenantCustomers, useCustomerDetail } from "../lib/tenantCustomers";
import { LogoUploader } from "../components/LogoUploader";
import { BrandedQrPoster } from "../components/BrandedQrPoster";
import { MercadoPagoConfigEditor } from "../components/MercadoPagoConfigEditor";
import { PaymentCheckoutButton } from "../components/PaymentCheckoutButton";
import { SubscriptionCheckoutButton } from "../components/SubscriptionCheckoutButton";
import { clearImpersonatedTenant, useImpersonatedTenant } from "../lib/impersonation";
import { StaffTab } from "../components/StaffTab";
import { KpiScorecard } from "../components/KpiScorecard";
import { OperationalConfigEditor } from "../components/OperationalConfigEditor";
import { SalesTab } from "../components/SalesTab";
import { TenantShareKit } from "../components/TenantShareKit";
import { GeofenceEditor } from "../components/GeofenceEditor";
import { BoletaImageViewer } from "../components/BoletaImageViewer";

type Tab = "summary" | "customers" | "sales" | "ambassadors" | "staff" | "settings";

/**
 * Vista del dueño/admin del comercio. UI completa con empty states para M2.5.
 * Wire-up real:
 *   - Stats: M4 (tickets) + M5 (ambassadors + commissions).
 *   - Applications: M5 (ambassadorApplications collection).
 *   - Ambassadors: M5.
 *   - Settings: M7 (configuración del tenant).
 */
export function TenantAdmin() {
  const nav = useNavigate();
  const { tenant } = useTenant();
  const { claims } = useAuth();
  const impersonated = useImpersonatedTenant();
  const isImpersonating = claims.role === "platform_admin" && !!impersonated;
  const [params] = useSearchParams();
  const initialTab = (params.get("tab") as Tab) ?? "summary";
  const [tab, setTab] = useState<Tab>(initialTab);
  useEffect(() => {
    const t = params.get("tab") as Tab | null;
    if (t) setTab(t);
  }, [params]);

  if (claims.role !== "tenant_admin" && claims.role !== "platform_admin") {
    return (
      <main className="min-h-dvh bg-cream">
        <div className="container-page text-center">
          <p className="text-sm text-leaf-700">No tienes acceso al panel admin.</p>
          <button onClick={() => nav("/")} className="btn-primary mt-4">
            Ir al inicio
          </button>
        </div>
      </main>
    );
  }

  return (
    <>
      {isImpersonating && (
        <div className="sticky top-0 z-30 bg-purple-600 px-4 py-2 text-center text-sm font-semibold text-white shadow-md">
          <div className="mx-auto flex max-w-md items-center justify-between gap-2">
            <span>
              Operando como admin de <strong>{tenant?.name ?? impersonated}</strong> (super user)
            </span>
            <button
              onClick={() => {
                clearImpersonatedTenant();
                nav("/platform");
              }}
              className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-purple-700 hover:bg-purple-50"
            >
              Salir
            </button>
          </div>
        </div>
      )}
      <TopBar />
      <main className="bg-cream pb-16 pt-4">
        <div className="mx-auto flex max-w-md flex-col gap-5 px-5">
          <header className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Panel admin</p>
              <h1 className="mt-1 font-display text-2xl font-semibold text-leaf-950">
                {tenant?.name ?? "Tu comercio"}
              </h1>
              <p className="mt-1 text-sm text-leaf-700">Gestiona tu programa de fidelización.</p>
            </div>
            <a
              href={tenant ? `/preview-cliente?tenant=${encodeURIComponent(tenant.tenantId)}` : "/preview-cliente"}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 rounded-full border border-leaf-200 bg-white px-3 py-2 text-xs font-medium text-leaf-700 hover:bg-leaf-50"
            >
              Ver como cliente final
            </a>
          </header>

          <nav className="flex gap-1 overflow-x-auto rounded-2xl bg-white p-1 ring-1 ring-leaf-100">
            <TabBtn label="Resumen" active={tab === "summary"} onClick={() => setTab("summary")} />
            <TabBtn label="Clientes" active={tab === "customers"} onClick={() => setTab("customers")} />
            <TabBtn label="Ventas" active={tab === "sales"} onClick={() => setTab("sales")} />
            <TabBtn label="Embajadores" active={tab === "ambassadors"} onClick={() => setTab("ambassadors")} />
            <TabBtn label="Staff" active={tab === "staff"} onClick={() => setTab("staff")} />
            <TabBtn label="Ajustes" active={tab === "settings"} onClick={() => setTab("settings")} />
          </nav>

          {tab === "summary" && <SummaryTab onGoTab={setTab} />}
          {tab === "customers" && <CustomersTab />}
          {tab === "sales" && <SalesTab />}
          {tab === "ambassadors" && <AmbassadorsTab />}
          {tab === "staff" && <StaffTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </main>
    </>
  );
}

function TabBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 rounded-xl px-3 py-2 text-sm font-medium transition ${
        active ? "bg-leaf-700 text-white" : "text-leaf-700 hover:bg-leaf-50"
      }`}
    >
      {label}
    </button>
  );
}

function SummaryTab({ onGoTab }: { onGoTab: (t: "ambassadors" | "settings") => void }) {
  const stats = useTenantStats();
  const recent = useTenantRecentActivity(8);
  const loyalty = useLoyaltyConfig();

  const formatCLP = (n: number) =>
    new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);

  const isEmpty = stats.totalCustomers === 0 && stats.totalPurchases === 0;
  const onboardDismissed = typeof window !== "undefined" && localStorage.getItem("fideliza:onboard-dismissed") === "1";

  return (
    <>
      {isEmpty && !onboardDismissed && (
        <section className="card bg-leaf-50/60 ring-leaf-200">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs uppercase tracking-wider text-leaf-700">Primera vez aqui</p>
              <h3 className="mt-1 font-display text-lg font-semibold text-leaf-950">
                3 pasos para arrancar
              </h3>
            </div>
            <button
              onClick={() => { localStorage.setItem("fideliza:onboard-dismissed", "1"); window.location.reload(); }}
              className="text-xs text-leaf-500 hover:text-leaf-700"
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>
          <ol className="mt-3 flex flex-col gap-2 text-sm text-leaf-800">
            <li className="flex gap-2">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-leaf-700 text-xs font-bold text-white">1</span>
              <span>
                Andá a <strong>Ajustes</strong> y descargá tu QR brandeado para imprimir y poner en la caja.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-leaf-700 text-xs font-bold text-white">2</span>
              <span>
                Invita a tu <strong>cajero</strong> desde el tab Staff (link por WhatsApp, 1 minuto).
              </span>
            </li>
            <li className="flex gap-2">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-leaf-700 text-xs font-bold text-white">3</span>
              <span>
                Comparte tu local en redes desde Ajustes (WhatsApp + IG + TikTok pre-armado).
              </span>
            </li>
          </ol>
          <button
            onClick={() => onGoTab("settings")}
            className="btn-primary mt-4 w-full text-sm"
          >
            Ir a Ajustes
          </button>
        </section>
      )}
      <KpiScorecard />
      <section className="grid grid-cols-2 gap-2">
        <StatTile label="Clientes" value={stats.totalCustomers} />
        <StatTile label="Compras aprobadas" value={stats.totalPurchases} />
        <StatTile label="Ventas totales" value={formatCLP(stats.totalRevenue)} />
        <StatTile label="Tickets activos" value={stats.unredeemedTickets} />
        <StatTile label="Rewards canjeados" value={stats.totalRewards} />
        <StatTile label="Embajadores" value={stats.totalAmbassadors} />
        <StatTile label="Comisiones pagadas" value={formatCLP(stats.totalCommissions)} />
        <StatTile label="Postulaciones" value={stats.pendingApplications} highlight={stats.pendingApplications > 0} />
      </section>

      {stats.pendingApplications > 0 && (
        <section
          className="cursor-pointer rounded-3xl bg-amber-50 p-4 ring-1 ring-amber-200 transition hover:bg-amber-100"
          onClick={() => onGoTab("ambassadors")}
        >
          <p className="text-xs uppercase tracking-wider text-amber-800">Atención</p>
          <p className="mt-1 text-sm text-amber-950">
            Tienes <strong>{stats.pendingApplications}</strong> {stats.pendingApplications === 1 ? "postulación a embajador" : "postulaciones a embajador"} esperando tu revisión.
          </p>
        </section>
      )}

      <section className="card flex flex-col gap-3">
        <p className="text-xs uppercase tracking-wider text-leaf-600">Configuración actual</p>
        <div className="grid grid-cols-1 gap-2">
          <ConfigRow
            label="Programa de loyalty"
            value={`${loyalty.ticketsForReward} compras = ${loyalty.rewardLabel}`}
            onEdit={() => onGoTab("settings")}
          />
          <ConfigRow
            label="QR de tu comercio"
            value="Imprimir y compartir"
            onEdit={() => onGoTab("settings")}
          />
          <ConfigRow
            label="Lectura automatica de boletas"
            value="Validación automática"
            onEdit={() => onGoTab("settings")}
          />
        </div>
      </section>

      <section className="card flex flex-col gap-3">
        <p className="text-xs uppercase tracking-wider text-leaf-600">Actividad reciente</p>
        {recent.length === 0 ? (
          <p className="text-sm text-leaf-600">
            Aún no hay actividad. Comparte tu QR con tus clientes para que empiecen a sumar tickets.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {recent.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between rounded-2xl bg-leaf-50/50 px-3 py-2 text-sm"
              >
                <span className="text-leaf-900">
                  {r.kind === "purchase" && "🧾 "}
                  {r.kind === "application" && "🌟 "}
                  {r.kind === "redeem" && "🎁 "}
                  {r.label}
                </span>
                <span className="text-xs text-leaf-500">
                  {r.at.toLocaleDateString("es-CL", { day: "2-digit", month: "short" })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function CustomersTab() {
  const { customers, loading } = useTenantCustomers();
  const [selectedUid, setSelectedUid] = useState<string | null>(null);

  if (selectedUid) {
    return <CustomerDetailPanel uid={selectedUid} onBack={() => setSelectedUid(null)} />;
  }

  return (
    <>
      <section className="card flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-leaf-600">Total</p>
          <p className="font-display text-2xl font-semibold text-leaf-950">{customers.length}</p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-leaf-600">Embajadores</p>
          <p className="font-display text-2xl font-semibold text-leaf-950">
            {customers.filter((c) => c.role === "ambassador").length}
          </p>
        </div>
      </section>

      {loading ? (
        <p className="text-sm text-leaf-600">Cargando…</p>
      ) : customers.length === 0 ? (
        <EmptyCard
          title="Aún no tienes clientes"
          body="Comparte tu QR con tus clientes para que empiecen a sumar tickets y aparezcan aquí."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {customers.map((c) => (
            <li key={c.id}>
              <button
                onClick={() => setSelectedUid(c.id)}
                className="flex w-full items-center gap-3 rounded-2xl border border-leaf-100 bg-white p-3 text-left transition hover:bg-leaf-50"
              >
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-leaf-100 font-display font-semibold text-leaf-800">
                  {(c.displayName ?? c.email ?? "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-leaf-950">
                    {c.displayName ?? c.email ?? "Sin nombre"}
                  </p>
                  <p className="truncate text-xs text-leaf-600">{c.email ?? "Cuenta anónima"}</p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2 py-1 text-[10px] uppercase tracking-wider ${
                    c.role === "ambassador"
                      ? "bg-leaf-100 text-leaf-800"
                      : c.role === "tenant_admin"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {c.role === "ambassador" ? "embajador" : c.role === "tenant_admin" ? "admin" : "cliente"}
                </span>
                <svg className="h-4 w-4 shrink-0 text-leaf-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function CustomerDetailPanel({ uid, onBack }: { uid: string; onBack: () => void }) {
  const { claims } = useAuth();
  const detail = useCustomerDetail(uid);
  const [impBusy, setImpBusy] = useState(false);
  const formatCLP = (n: number) =>
    new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);

  const impersonate = async () => {
    setImpBusy(true);
    try {
      const res = await api.impersonateCustomer({ uid });
      sessionStorage.setItem("fideliza:impersonate-customer-token", res.data.customToken);
      window.open("/impersonate", "_blank", "noopener");
    } catch (e) {
      alert(callableErrorMessage(e));
    } finally {
      setImpBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <button onClick={onBack} className="btn-ghost px-3 py-1.5 text-xs">
          ← Volver a la lista
        </button>
        {claims.role === "platform_admin" && (
          <button
            onClick={impersonate}
            disabled={impBusy}
            className="rounded-full border border-purple-300 bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-800 hover:bg-purple-100 disabled:opacity-50"
          >
            {impBusy ? "Generando…" : "Ver como este cliente"}
          </button>
        )}
      </div>

      {detail.loading ? (
        <p className="text-sm text-leaf-600">Cargando…</p>
      ) : !detail.user ? (
        <p className="text-sm text-red-700">Cliente no encontrado.</p>
      ) : (
        <>
          <section className="card flex items-start gap-3">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-leaf-100 font-display text-xl font-semibold text-leaf-800">
              {(detail.user.displayName ?? detail.user.email ?? "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-lg font-semibold text-leaf-950">
                {detail.user.displayName ?? "Sin nombre"}
              </p>
              <p className="truncate text-sm text-leaf-700">{detail.user.email ?? "Cuenta anónima"}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wider text-leaf-500">
                {detail.user.role}
                {detail.user.createdAt &&
                  ` · Cliente desde ${detail.user.createdAt.toLocaleDateString("es-CL")}`}
              </p>
            </div>
          </section>

          <section className="grid grid-cols-2 gap-2">
            <StatTile label="Compras" value={detail.totalPurchases} />
            <StatTile label="Gastado" value={formatCLP(detail.totalSpent)} />
            <StatTile label="Tickets activos" value={detail.ticketsUnredeemed} />
            <StatTile label="Rewards canjeados" value={detail.rewardsClaimed} />
          </section>

          <section className="card flex flex-col gap-3">
            <p className="text-xs uppercase tracking-wider text-leaf-600">Últimas compras</p>
            {detail.recentPurchases.length === 0 ? (
              <p className="text-sm text-leaf-600">Aún no tiene compras registradas.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {detail.recentPurchases.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center gap-3 rounded-xl bg-leaf-50/50 px-3 py-2 text-sm"
                  >
                    <BoletaImageViewer
                      storagePath={p.voucherStoragePath}
                      imageMissing={p.imageMissing}
                      alt={`Boleta ${formatCLP(p.amount)}`}
                    />
                    <div className="flex flex-1 items-center justify-between">
                      <span className="text-leaf-900">{formatCLP(p.amount)}</span>
                      <span className="text-xs text-leaf-600">
                        {p.status} ·{" "}
                        {p.createdAt
                          ? p.createdAt.toLocaleDateString("es-CL", { day: "2-digit", month: "short" })
                          : "—"}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </>
  );
}

function ConfigRow({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-leaf-100 bg-white p-3">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-leaf-600">{label}</p>
        <p className="truncate text-sm font-medium text-leaf-900">{value}</p>
      </div>
      <button onClick={onEdit} className="btn-ghost px-3 py-1.5 text-xs">
        Configurar
      </button>
    </div>
  );
}

function ApplicationsTab() {
  const [filter, setFilter] = useState<"pending" | "approved" | "rejected" | "all">("pending");
  const applications = useAmbassadorApplications(filter);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const review = async (applicationId: string, decision: "approve" | "reject") => {
    setBusyId(applicationId);
    setFeedback(null);
    try {
      const res = await api.reviewAmbassadorApplication({ applicationId, decision });
      if (res.data.decision === "approved")
        setFeedback(`✓ Aprobado. Código generado: ${res.data.code}`);
      else setFeedback("✓ Rechazado.");
      setTimeout(() => setFeedback(null), 4000);
    } catch (err) {
      setFeedback(`✗ ${callableErrorMessage(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <section className="flex gap-1 overflow-x-auto">
        {(["pending", "approved", "rejected", "all"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition ${
              filter === f
                ? "bg-leaf-700 text-white"
                : "bg-white text-leaf-700 ring-1 ring-leaf-100"
            }`}
          >
            {f === "pending" ? "Pendientes" : f === "approved" ? "Aprobadas" : f === "rejected" ? "Rechazadas" : "Todas"}
          </button>
        ))}
      </section>

      {feedback && (
        <div className="rounded-2xl bg-leaf-50 px-4 py-3 text-sm text-leaf-900 ring-1 ring-leaf-200">
          {feedback}
        </div>
      )}

      {applications.length === 0 ? (
        <EmptyCard
          title="No hay postulaciones"
          body="Cuando alguien se postule, vas a verlo aquí para aprobar o rechazar."
        />
      ) : (
        applications.map((app) => (
          <article key={app.id} className="card flex flex-col gap-2">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="truncate font-semibold text-leaf-950">{app.name}</p>
                <p className="truncate text-xs text-leaf-600">{app.email}</p>
              </div>
              <StatusBadge status={app.status} />
            </div>
            <p className="text-sm text-leaf-700">
              <span className="font-mono">{app.instagram}</span>
              {app.followers ? ` · ${app.followers} seguidores` : ""}
            </p>
            {app.message && (
              <p className="rounded-xl bg-leaf-50/60 px-3 py-2 text-sm text-leaf-800">
                "{app.message}"
              </p>
            )}
            <p className="text-[11px] text-leaf-500">
              Postulado {app.createdAt.toLocaleDateString("es-CL")}
            </p>
            {app.status === "pending" && !app.hasAccount && (
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200">
                Sin cuenta activa en Fideliza (dato migrado). No se puede aprobar directo —
                invítala por email primero desde la pestaña "Invitaciones".
              </p>
            )}
            {app.status === "pending" && (
              <div className="flex gap-2">
                <button
                  onClick={() => review(app.id, "reject")}
                  disabled={busyId === app.id}
                  className="btn-secondary flex-1 py-2 text-sm disabled:opacity-50"
                >
                  Rechazar
                </button>
                <button
                  onClick={() => review(app.id, "approve")}
                  disabled={busyId === app.id || !app.hasAccount}
                  title={!app.hasAccount ? "Sin cuenta activa: invítala por email primero" : undefined}
                  className="btn-primary flex-1 py-2 text-sm disabled:opacity-50"
                >
                  {busyId === app.id ? "Procesando…" : "Aprobar"}
                </button>
              </div>
            )}
          </article>
        ))
      )}
    </>
  );
}

function AmbassadorsTab() {
  const [sub, setSub] = useState<"activos" | "postulaciones" | "invitaciones">("activos");
  const { ambassadors } = useTenantAmbassadors();
  const { invites } = useTenantInvites("ambassador");
  const pendingInvites = invites.filter((i) => !i.used);
  const formatCLP = (n: number) =>
    new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);

  return (
    <>
      <nav className="flex gap-1 rounded-2xl bg-white p-1 ring-1 ring-leaf-100">
        <SubTab label={`Activos (${ambassadors.length})`} active={sub === "activos"} onClick={() => setSub("activos")} />
        <SubTab label="Postulaciones" active={sub === "postulaciones"} onClick={() => setSub("postulaciones")} />
        <SubTab label={`Invitaciones (${pendingInvites.length})`} active={sub === "invitaciones"} onClick={() => setSub("invitaciones")} />
      </nav>

      {sub === "activos" && (
        <>
          {ambassadors.length === 0 ? (
            <EmptyCard
              title="Aún no tienes embajadores"
              body="Cuando aceptes postulaciones o invites a alguien, aparecerá aquí con sus stats (ventas referidas, comisión, códigos activos)."
            />
          ) : (
            ambassadors.map((amb) => (
              <article key={amb.id} className="card flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-leaf-100 font-display text-lg font-semibold text-leaf-800">
                    {amb.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-leaf-950">{amb.name}</p>
                    <p className="truncate text-xs text-leaf-600">
                      {amb.instagram}
                      {amb.followers ? ` · ${amb.followers} seguidores` : ""}
                    </p>
                    {amb.email && <p className="truncate text-[11px] text-leaf-500">{amb.email}</p>}
                    {amb.phone && (
                      <a
                        href={`https://wa.me/${amb.phone.replace(/[^0-9]/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-green-700 hover:underline"
                      >
                        WhatsApp {amb.phone}
                      </a>
                    )}
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2 py-1 text-[10px] uppercase tracking-wider ${
                      amb.active ? "bg-leaf-100 text-leaf-800" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {amb.active ? "activo" : "pausado"}
                  </span>
                </div>

                {amb.activeCodes.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {amb.activeCodes.map((c) => (
                      <span
                        key={c.code}
                        className="rounded-full bg-leaf-50 px-2 py-1 font-mono text-[10px] text-leaf-800 ring-1 ring-leaf-200"
                      >
                        {c.code} · {c.discountPercent}% · {c.timesUsed} usos
                      </span>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-xl bg-leaf-50/60 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wider text-leaf-600">N° ventas</p>
                    <p className="font-display text-base font-semibold text-leaf-950">{amb.salesCount}</p>
                  </div>
                  <div className="rounded-xl bg-leaf-50/60 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wider text-leaf-600">Ventas $</p>
                    <p className="font-display text-base font-semibold text-leaf-950">
                      {formatCLP(amb.totalSales)}
                    </p>
                  </div>
                  <div className="rounded-xl bg-leaf-50/60 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wider text-leaf-600">Comisión</p>
                    <p className="font-display text-base font-semibold text-leaf-950">
                      {formatCLP(amb.totalCommissions)}
                    </p>
                  </div>
                </div>
              </article>
            ))
          )}
        </>
      )}

      {sub === "postulaciones" && <ApplicationsTab />}

      {sub === "invitaciones" && (
        <>
          <section className="card">
            <p className="text-xs uppercase tracking-wider text-leaf-600">¿Invitar a alguien?</p>
            <h3 className="mt-1 font-display text-base font-semibold text-leaf-950">
              Invita por email
            </h3>
            <p className="mt-1 text-sm text-leaf-700">
              Manda invitación directa para que se sume como embajador sin postularse.
            </p>
            <InviteAmbassadorForm />
          </section>
          {pendingInvites.length === 0 ? (
            <EmptyCard title="Sin invitaciones pendientes" body="Las invitaciones que mandes aparecen aquí hasta que se acepten o caduquen." />
          ) : null}
        </>
      )}

      {sub === "invitaciones" && pendingInvites.length > 0 && (
        <section className="card flex flex-col gap-2">
          <p className="text-xs uppercase tracking-wider text-leaf-600">
            Invitaciones enviadas · pendientes
          </p>
          <ul className="flex flex-col gap-2">
            {pendingInvites.map((inv) => (
              <li
                key={inv.id}
                className="flex items-center justify-between gap-2 rounded-2xl bg-amber-50 px-3 py-2 text-sm ring-1 ring-amber-200"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-amber-950">{inv.email}</p>
                  <p className="text-[10px] uppercase tracking-wider text-amber-700">
                    {inv.createdAt
                      ? `Enviado ${inv.createdAt.toLocaleDateString("es-CL", { day: "2-digit", month: "short" })}`
                      : "Pendiente"}
                    {inv.expiresAt && ` · expira ${inv.expiresAt.toLocaleDateString("es-CL", { day: "2-digit", month: "short" })}`}
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-amber-200 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-900">
                  Pendiente
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function SettingsQuickNav({
  onJump,
  onTenantAction,
  isPlatformAdmin
}: {
  onJump: (anchor: string) => void;
  onTenantAction: (href: string) => void;
  isPlatformAdmin: boolean;
}) {
  const chips: Array<{ label: string; action: () => void }> = [
    { label: "Compartir local", action: () => onJump("anchor-share") },
    { label: "QR para imprimir", action: () => onJump("anchor-qr") },
    { label: "Rewards", action: () => onJump("anchor-rewards") },
    { label: "Cajero y comisiones", action: () => onJump("anchor-cashier") },
    { label: "Lectura de boletas", action: () => onJump("anchor-receipts") },
    { label: "Diseno de la app", action: () => onJump("anchor-branding") },
    { label: "Multi-local", action: () => onJump("anchor-geofence") },
    { label: "Agregar cajero", action: () => onTenantAction("/admin?tab=staff") },
    { label: "Embajadores", action: () => onTenantAction("/admin?tab=ambassadors") }
  ];
  if (isPlatformAdmin) chips.push({ label: "Mercado Pago", action: () => onJump("anchor-mp") });

  return (
    <section className="card flex flex-col gap-2">
      <p className="text-xs uppercase tracking-wider text-leaf-600">Saltar a</p>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <button
            key={c.label}
            onClick={c.action}
            className="rounded-full bg-leaf-50 px-3 py-1.5 text-xs font-medium text-leaf-800 ring-1 ring-leaf-200 hover:bg-leaf-100"
          >
            {c.label}
          </button>
        ))}
      </div>
    </section>
  );
}

function SubTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-xl px-2 py-1.5 text-xs font-medium transition ${
        active ? "bg-leaf-700 text-white" : "text-leaf-700 hover:bg-leaf-50"
      }`}
    >
      {label}
    </button>
  );
}

function SettingsTab() {
  const { tenant } = useTenant();
  const { user, claims } = useAuth();
  const ocrConfig = useTenantOcrConfig();
  const loyaltyConfig = useLoyaltyConfig();
  const nav = useNavigate();

  const goTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <SettingsQuickNav onJump={goTo} onTenantAction={(href) => nav(href)} isPlatformAdmin={claims.role === "platform_admin"} />

      {tenant && (
        <section id="anchor-share" className="card flex flex-col gap-3 scroll-mt-32">
          <div>
            <p className="text-xs uppercase tracking-wider text-leaf-600">Difusion online</p>
            <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
              Compartir tu local en redes
            </h2>
            <p className="mt-1 text-sm text-leaf-700">
              Texto pre-armado listo para WhatsApp, Instagram caption o TikTok caption.
            </p>
          </div>
          <TenantShareKit
            tenantId={tenant.tenantId}
            tenantName={tenant.name}
            rewardLabel={loyaltyConfig.rewardLabel ?? "1 reward"}
            ticketsForReward={loyaltyConfig.ticketsForReward ?? 5}
          />
        </section>
      )}

      {tenant && (
        <section id="anchor-qr" className="card flex flex-col gap-3 scroll-mt-32">
          <div>
            <p className="text-xs uppercase tracking-wider text-leaf-600">QR brandeado para imprimir</p>
            <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
              Poster listo para tu caja
            </h2>
            <p className="mt-1 text-sm text-leaf-700">
              Incluye tu logo, color, nombre y el reward. Elige formato y descargá en alta
              resolución (300 DPI).
            </p>
          </div>
          <BrandedQrPoster
            tenantId={tenant.tenantId}
            tenantName={tenant.name}
            primaryColor={tenant.theme.primary}
            logoUrl={tenant.theme.logoUrl}
            ticketsForReward={loyaltyConfig.ticketsForReward}
            rewardLabel={loyaltyConfig.rewardLabel}
          />
        </section>
      )}

      {tenant && (
        <section className="card flex flex-col gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-leaf-600">QR simple (compartir digital)</p>
            <h2 className="mt-1 font-display text-base font-semibold text-leaf-950">
              Para WhatsApp / redes
            </h2>
          </div>
          <TenantQR tenantId={tenant.tenantId} tenantName={tenant.name} size={240} />
        </section>
      )}

      {tenant && (
        <section id="anchor-rewards" className="card flex flex-col gap-3 scroll-mt-32">
          <div>
            <p className="text-xs uppercase tracking-wider text-leaf-600">Programa de loyalty</p>
            <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
              Cuántas compras = 1 reward
            </h2>
          </div>
          {!loyaltyConfig.loading && (
            <LoyaltyConfigEditor
              tenantId={tenant.tenantId}
              initialTickets={loyaltyConfig.ticketsForReward}
              initialReward={loyaltyConfig.rewardLabel}
              initialRewardItem={loyaltyConfig.rewardItem}
            />
          )}
        </section>
      )}

      {tenant && (
        <section id="anchor-cashier" className="card flex flex-col gap-3 scroll-mt-32">
          <div>
            <p className="text-xs uppercase tracking-wider text-leaf-600">Operacion del cajero</p>
            <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
              Categorias, descuento, vouchers
            </h2>
            <p className="mt-1 text-sm text-leaf-700">
              Define que cobra el cajero, cuanto descuento por defecto y cuanto vive el QR.
            </p>
          </div>
          <OperationalConfigEditor
            tenantId={tenant.tenantId}
            initialCategories={(tenant as unknown as { categories?: string[] }).categories ?? []}
            initialRewardIcon={(loyaltyConfig as unknown as { rewardIcon?: string }).rewardIcon ?? "☕"}
          />
        </section>
      )}

      {tenant && user && (
        <section id="anchor-receipts" className="card flex flex-col gap-3 scroll-mt-32">
          <div>
            <p className="text-xs uppercase tracking-wider text-leaf-600">Lectura automatica de boletas</p>
            <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
              Ensenale al sistema como son tus boletas
            </h2>
            <p className="mt-1 text-sm text-leaf-700">
              Cuando un cliente sube una foto de su boleta (sin pasar por cajero), el sistema
              la lee automaticamente para validar que sea tuya. Mientras mas boletas reales
              subas aqui de ejemplo, mejor reconoce productos, precios y formato de tu local.
            </p>
            <p className="mt-1 text-[11px] text-leaf-500">
              Si trabajas solo con cajero asistido (cliente muestra QR), puedes saltar esto.
            </p>
          </div>
          <OcrTrainer
            tenantId={tenant.tenantId}
            uid={user.uid}
            existingIdentifiers={ocrConfig.identifiers}
            existingTaxIdHints={ocrConfig.taxIdHints}
            existingProducts={ocrConfig.knownProducts}
            existingPrices={ocrConfig.validPrices}
          />
        </section>
      )}

      {tenant && !ocrConfig.loading && (
        <section className="card flex flex-col gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-leaf-600">Ajustes avanzados de lectura</p>
            <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
              Que palabras o precios reconocer
            </h2>
            <p className="mt-1 text-sm text-leaf-700">
              Lista manual de palabras, RUTs, productos y precios validos. Si subiste una boleta
              de ejemplo arriba, esto ya se llena solo. Edita aqui para ajustar a mano.
            </p>
          </div>
          <OcrManualEditor
            tenantId={tenant.tenantId}
            initialIdentifiers={ocrConfig.identifiers}
            initialTaxIds={ocrConfig.taxIdHints}
            initialProducts={ocrConfig.knownProducts}
            initialPrices={ocrConfig.validPrices}
          />
        </section>
      )}

      {tenant && (
        <section id="anchor-branding" className="card flex flex-col gap-3 scroll-mt-32">
          <div>
            <p className="text-xs uppercase tracking-wider text-leaf-600">Layout de la tienda</p>
            <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
              Logo, nombre, color
            </h2>
          </div>
          <LogoUploader
            tenantId={tenant.tenantId}
            currentLogoUrl={tenant.theme.logoUrl}
            fallbackColor={tenant.theme.primary}
          />
          <BrandingEditor
            tenantId={tenant.tenantId}
            initialName={tenant.name}
            initialPrimary={tenant.theme.primary}
          />
        </section>
      )}

      {tenant && claims.role === "platform_admin" && (
        <div id="anchor-mp" className="scroll-mt-32" />
      )}
      {tenant && claims.role === "platform_admin" && (
        <section className="card flex flex-col gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-leaf-600">Solo platform admin</p>
            <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
              Mercado Pago (cobro de suscripciones Fideliza)
            </h2>
            <p className="mt-1 text-sm text-leaf-700">
              Configura la cuenta MP donde Fideliza recibe los pagos de suscripciones de los tenants. No es visible para tenant admins.
            </p>
          </div>
          <MercadoPagoConfigEditor
            tenantId={tenant.tenantId}
            defaultFantasyName={tenant.name.slice(0, 13).toUpperCase()}
          />
          <div className="mt-2 flex flex-col gap-4 border-t border-leaf-100 pt-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-leaf-600">Smoke test · Suscripcion</p>
              <p className="mt-1 mb-3 text-sm text-leaf-700">
                Crea una suscripcion real de $1.000 CLP/mes (minimo MP Chile es $950).
                Valida preapproval + webhook. Cancela manual desde tu cuenta MP cuando termines.
              </p>
              <SubscriptionCheckoutButton
                tenantId={tenant.tenantId}
                amount={1000}
                reason="Smoke test Fideliza Pro"
                buttonLabel="Probar suscripcion ($1.000/mes)"
              />
            </div>
            <div className="border-t border-leaf-100 pt-3">
              <p className="text-xs uppercase tracking-wider text-leaf-600">Smoke test · Pago unico</p>
              <p className="mt-1 mb-3 text-sm text-leaf-700">
                Solo funciona si tu app MP esta configurada en modo Checkout Pro (no Suscripciones).
              </p>
              <PaymentCheckoutButton
                tenantId={tenant.tenantId}
                amount={100}
                description="Smoke test Fideliza"
                buttonLabel="Probar checkout ($100)"
              />
            </div>
          </div>
        </section>
      )}

      <section className="card flex flex-col gap-3">
        <p className="text-xs uppercase tracking-wider text-leaf-600">Datos tecnicos del comercio</p>
        <SettingRow
          label="ID del tenant"
          value={<code className="text-xs">{tenant?.tenantId ?? "—"}</code>}
          hint="Identificador interno. No se puede cambiar."
        />
      </section>

      {tenant && (
        <section id="anchor-geofence" className="card flex flex-col gap-3 scroll-mt-32">
          <div>
            <p className="text-xs uppercase tracking-wider text-leaf-600">Multi-local</p>
            <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
              Donde queda tu negocio
            </h2>
            <p className="mt-1 text-sm text-leaf-700">
              Define tus locales con un radio en metros. Util si en el futuro queres restringir
              cobros / boletas a clientes que esten fisicamente cerca.
            </p>
          </div>
          <GeofenceEditor tenantId={tenant.tenantId} />
        </section>
      )}
    </>
  );
}

function InviteAmbassadorForm() {
  const { tenant } = useTenant();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenant?.tenantId) return;
    setBusy(true);
    setError(null);
    try {
      await api.inviteTenantAdmin({ tenantId: tenant.tenantId, email, role: "ambassador" });
      setSent(true);
      setEmail("");
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="mt-3 flex flex-col gap-2">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="email del embajador"
        className="input text-sm"
      />
      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </p>
      )}
      {sent && (
        <p className="rounded-xl border border-leaf-200 bg-leaf-50 px-3 py-2 text-xs text-leaf-800">
          Invitación enviada ✓
        </p>
      )}
      <button type="submit" disabled={busy} className="btn-primary py-2 text-sm">
        {busy ? "Enviando…" : "Enviar invitación"}
      </button>
    </form>
  );
}

function StatTile({ label, value, highlight = false }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className={`rounded-2xl p-4 ring-1 ${highlight ? "bg-amber-50 ring-amber-200" : "bg-white ring-leaf-100"}`}>
      <p className={`text-[10px] uppercase tracking-wider ${highlight ? "text-amber-800" : "text-leaf-600"}`}>{label}</p>
      <p className={`mt-1 font-display text-2xl font-semibold ${highlight ? "text-amber-950" : "text-leaf-950"}`}>{value}</p>
    </div>
  );
}

function SettingRow({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-leaf-600">{label}</p>
      <div className="mt-1 text-sm text-leaf-950">{value}</div>
      {hint && <p className="mt-1 text-xs text-leaf-500">{hint}</p>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-leaf-100 text-leaf-800",
    rejected: "bg-red-100 text-red-700"
  };
  return (
    <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-wider ${styles[status] ?? styles.pending}`}>
      {status}
    </span>
  );
}

function EmptyCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="card text-center">
      <p className="font-display text-base font-semibold text-leaf-950">{title}</p>
      <p className="mt-2 text-sm text-leaf-700">{body}</p>
    </div>
  );
}
